import type { ExerciseDef } from './types'

// ─── 4.5 Minima, maxima & saddles ─────────────────────────────────────────────

export const classify: ExerciseDef = {
  starter: `// The second derivative is the slope of the slope: how fast the slope itself changes.
// It can be estimated with three nearby points:  f''(x) ≈ (f(x + h) − 2f(x) + f(x − h)) / h²

// 1. secondDerivative(f, x)   (use h = 1e-4)
function secondDerivative(f: (x: number) => number, x: number): number {
  return 0
}

// 2. classify(f, x): x is a flat spot (slope 0). Is it a "minimum", a "maximum", or "neither"?
//    Curving up (f'' > 0.001) → minimum.  Curving down (f'' < −0.001) → maximum.  Otherwise → neither.
function classify(f: (x: number) => number, x: number): "minimum" | "maximum" | "neither" {
  return "neither"
}

const f = (x: number) => x ** 3 - 3 * x     // flat at x = −1 and x = 1
console.log(classify(f, -1), classify(f, 1), classify(x => x ** 3, 0))
`,
  solution: `function secondDerivative(f: (x: number) => number, x: number): number {
  const h = 1e-4
  return (f(x + h) - 2 * f(x) + f(x - h)) / (h * h)
}

function classify(f: (x: number) => number, x: number): "minimum" | "maximum" | "neither" {
  const k = secondDerivative(f, x)
  if (k > 0.001) return "minimum"
  if (k < -0.001) return "maximum"
  return "neither"
}

const f = (x: number) => x ** 3 - 3 * x
console.log(classify(f, -1), classify(f, 1), classify(x => x ** 3, 0))
`,
  tests: `
test("x² bends up everywhere: f'' = 2", () => expect(secondDerivative(x => x * x, 3), "secondDerivative(x², 3)").toBeCloseTo(2, 1e-3))
test("x³ − 3x has f''(1) = 6", () => expect(secondDerivative(x => x ** 3 - 3 * x, 1), "f''(1)").toBeCloseTo(6, 1e-3))
test("x³ − 3x: x = 1 is a minimum", () => expect(classify(x => x ** 3 - 3 * x, 1), "classify(…, 1)").toBe("minimum"))
test("x³ − 3x: x = −1 is a maximum", () => expect(classify(x => x ** 3 - 3 * x, -1), "classify(…, −1)").toBe("maximum"))
test("x³ at 0 is neither", () => expect(classify(x => x ** 3, 0), "classify(x³, 0)").toBe("neither"))
`,
}

export const restarts: ExerciseDef = {
  starter: `// f(x) = x⁴/4 − x² + x/4 has two valleys. Its slope is f'(x) = x³ − 2x + 1/4.
const f = (x: number) => x ** 4 / 4 - x * x + x / 4
const slope = (x: number) => x ** 3 - 2 * x + 0.25

// 1. descend(x0): gradient descent from x0, 200 steps with learning rate 0.05. Return the final x.
function descend(x0: number): number {
  return x0
}

// 2. bestOfStarts(starts): run descend from every start; return the end point with the LOWEST f.
function bestOfStarts(starts: number[]): number {
  return starts[0]
}

console.log(descend(2), descend(-2), bestOfStarts([2, 0.5, -2]))
plot.fn(f, [-2.2, 2.2], { title: "two valleys — which one you find depends on where you start" })
`,
  solution: `const f = (x: number) => x ** 4 / 4 - x * x + x / 4
const slope = (x: number) => x ** 3 - 2 * x + 0.25

function descend(x0: number): number {
  let x = x0
  for (let step = 0; step < 200; step++) x -= 0.05 * slope(x)
  return x
}

function bestOfStarts(starts: number[]): number {
  let best = descend(starts[0])
  for (const s of starts) {
    const x = descend(s)
    if (f(x) < f(best)) best = x
  }
  return best
}

console.log(descend(2), descend(-2), bestOfStarts([2, 0.5, -2]))
plot.fn(f, [-2.2, 2.2], { title: "two valleys — which one you find depends on where you start" })
`,
  tests: `
test("from x = 2 it finds the right-hand valley (≈ 1.35)", () => expect(descend(2), "descend(2)").toBeBetween(1.3, 1.4))
test("from x = −2 it finds the deeper left-hand valley (≈ −1.47)", () => expect(descend(-2), "descend(-2)").toBeBetween(-1.5, -1.44))
test("the slope is ~0 where it stops", () => expect(Math.abs(x0slope(descend(0.9))), "|slope|").toBeLessThan(1e-4))
test("bestOfStarts picks the deepest valley", () => expect(bestOfStarts([2, 0.5, -2]), "bestOfStarts([2, 0.5, -2])").toBeBetween(-1.5, -1.44))
test("even when the good start is last", () => expect(bestOfStarts([1.8, 1.2, -0.3]), "bestOfStarts([1.8, 1.2, -0.3])").toBeBetween(-1.5, -1.44))
function x0slope(x) { return x ** 3 - 2 * x + 0.25 }
`,
}

export const hessian: ExerciseDef = {
  starter: `// With two inputs, "bend" becomes a 2×2 matrix of second derivatives — the Hessian:
//   [[∂²f/∂x²,  ∂²f/∂x∂y],
//    [∂²f/∂y∂x, ∂²f/∂y² ]]
// The "x∂y" entries: how the x-slope changes as you move in y.

// 1. hessian(f, x, y): estimate it numerically. Two of the formulas are given; the mixed one is yours:
//    ∂²f/∂x∂y ≈ (f(x+h, y+h) − f(x+h, y−h) − f(x−h, y+h) + f(x−h, y−h)) / (4h²)
function hessian(f: (x: number, y: number) => number, x: number, y: number): number[][] {
  const h = 1e-3
  const fxx = (f(x + h, y) - 2 * f(x, y) + f(x - h, y)) / (h * h)
  const fyy = (f(x, y + h) - 2 * f(x, y) + f(x, y - h)) / (h * h)
  const fxy = 0
  return [[fxx, fxy], [fxy, fyy]]
}

// 2. kind(f, x, y): at a flat point, use the determinant d = fxx·fyy − fxy²:
//    d < 0 → "saddle" (curves up one way, down the other)
//    d > 0 and fxx > 0 → "minimum";  d > 0 and fxx < 0 → "maximum"
function kind(f: (x: number, y: number) => number, x: number, y: number): "minimum" | "maximum" | "saddle" {
  return "minimum"
}

console.log(kind((x, y) => x * x + y * y, 0, 0), kind((x, y) => x * x - y * y, 0, 0))
`,
  solution: `function hessian(f: (x: number, y: number) => number, x: number, y: number): number[][] {
  const h = 1e-3
  const fxx = (f(x + h, y) - 2 * f(x, y) + f(x - h, y)) / (h * h)
  const fyy = (f(x, y + h) - 2 * f(x, y) + f(x, y - h)) / (h * h)
  const fxy = (f(x + h, y + h) - f(x + h, y - h) - f(x - h, y + h) + f(x - h, y - h)) / (4 * h * h)
  return [[fxx, fxy], [fxy, fyy]]
}

function kind(f: (x: number, y: number) => number, x: number, y: number): "minimum" | "maximum" | "saddle" {
  const [[fxx, fxy], [, fyy]] = hessian(f, x, y)
  const d = fxx * fyy - fxy * fxy
  if (d < 0) return "saddle"
  return fxx > 0 ? "minimum" : "maximum"
}

console.log(kind((x, y) => x * x + y * y, 0, 0), kind((x, y) => x * x - y * y, 0, 0))
`,
  tests: `
test("the mixed derivative of x·y is 1", () => expect(hessian((x, y) => x * y, 0.3, 0.7)[0][1], "∂²(xy)/∂x∂y").toBeCloseTo(1, 1e-4))
test("Hessian of x² + 3y² is [[2, 0], [0, 6]]", () => expect(hessian((x, y) => x * x + 3 * y * y, 1, 2), "hessian").toBeCloseTo([[2, 0], [0, 6]], 1e-3))
test("a bowl is a minimum", () => expect(kind((x, y) => x * x + y * y, 0, 0), "kind(bowl)").toBe("minimum"))
test("an upside-down bowl is a maximum", () => expect(kind((x, y) => -x * x - 2 * y * y, 0, 0), "kind(hill)").toBe("maximum"))
test("x² − y² is a saddle", () => expect(kind((x, y) => x * x - y * y, 0, 0), "kind(x² − y²)").toBe("saddle"))
test("x·y is a saddle too (it only shows up in the mixed term)", () => expect(kind((x, y) => x * y, 0, 0), "kind(xy)").toBe("saddle"))
`,
}

// ─── 4.6 Integrals ────────────────────────────────────────────────────────────

export const riemann: ExerciseDef = {
  starter: `// 1. area(f, a, b, n): add up n thin rectangles under f between a and b.
//    Width w = (b − a) / n. Use the height at each rectangle's MIDDLE.
function area(f: (x: number) => number, a: number, b: number, n = 1000): number {
  return 0
}

// 2. average(f, a, b): the average height of f between a and b — its area divided by the width.
function average(f: (x: number) => number, a: number, b: number): number {
  return 0
}

console.log(area(x => x * x, 0, 2))      // exact answer: 8/3 ≈ 2.6667
console.log(average(Math.sin, 0, Math.PI))

// The bell curve of probability (you'll meet it properly in Module 5).
// The area under it between −1 and 1 is the chance of landing within one "spread" of the middle:
const bell = (x: number) => Math.exp(-x * x / 2) / Math.sqrt(2 * Math.PI)
console.log("P(−1 < x < 1) ≈", area(bell, -1, 1))
`,
  solution: `function area(f: (x: number) => number, a: number, b: number, n = 1000): number {
  const w = (b - a) / n
  let total = 0
  for (let i = 0; i < n; i++) total += f(a + (i + 0.5) * w) * w
  return total
}

function average(f: (x: number) => number, a: number, b: number): number {
  return area(f, a, b) / (b - a)
}

console.log(area(x => x * x, 0, 2))
console.log(average(Math.sin, 0, Math.PI))

const bell = (x: number) => Math.exp(-x * x / 2) / Math.sqrt(2 * Math.PI)
console.log("P(−1 < x < 1) ≈", area(bell, -1, 1))
`,
  tests: `
test("area under x² from 0 to 2 is 8/3", () => expect(area(x => x * x, 0, 2), "area(x², 0, 2)").toBeCloseTo(8 / 3, 1e-5))
test("area under a constant 5 from 1 to 4 is 15", () => expect(area(() => 5, 1, 4), "area(5, 1, 4)").toBeCloseTo(15))
test("area below the axis counts as negative", () => expect(area(x => x, -2, 0), "area(x, −2, 0)").toBeCloseTo(-2, 1e-6))
test("the average of sin over half a turn is 2/π", () => expect(average(Math.sin, 0, Math.PI), "average(sin, 0, π)").toBeCloseTo(2 / Math.PI, 1e-5))
test("about 68.3% of the bell curve is within 1 of the middle", () => expect(area(x => Math.exp(-x * x / 2) / Math.sqrt(2 * Math.PI), -1, 1), "area").toBeCloseTo(0.682689, 1e-5))
`,
}

export const monteCarlo: ExerciseDef = {
  starter: `// Another way to measure an area: throw random darts and count the hits.
// A quarter circle of radius 1 sits inside the 1×1 square. Its area is π/4.

// estimatePi(n): throw n darts at the square (x and y from random.random()).
// The fraction that lands inside the circle (x² + y² ≤ 1) estimates π/4 — so multiply by 4.
function estimatePi(n: number): number {
  return 0
}

for (const n of [100, 1000, 10000, 100000]) console.log(n, "darts →", estimatePi(n))
`,
  solution: `function estimatePi(n: number): number {
  let inside = 0
  for (let i = 0; i < n; i++) {
    const x = random.random()
    const y = random.random()
    if (x * x + y * y <= 1) inside++
  }
  return (4 * inside) / n
}

for (const n of [100, 1000, 10000, 100000]) console.log(n, "darts →", estimatePi(n))
`,
  tests: `
test("100 000 darts get π right to about 2 decimal places", () => expect(estimatePi(100000), "estimatePi(100000)").toBeBetween(3.12, 3.16))
test("the answer is always between 0 and 4", () => expect(estimatePi(10), "estimatePi(10)").toBeBetween(0, 4))
`,
}

// ─── 4.7 Project: fit a curve ─────────────────────────────────────────────────

const THROW_DATA = `// A ball is thrown upwards. Here are noisy measurements of its height (m) every 0.1 s.
random.seed(7)
const times = range(23).map(i => i * 0.1)
const heights = times.map(t => 1.5 + 11 * t - 4.9 * t * t + random.normal(0, 0.15))`

export const fitLoss: ExerciseDef = {
  starter: `${THROW_DATA}
plot.scatter(times.map((t, i) => [t, heights[i]]), { title: "measured heights", xLabel: "time (s)", yLabel: "height (m)" })

// Our model: a parabola  h(t) = a + b·t + c·t²   with parameters p = [a, b, c].
// 1. model(p, t)
function model(p: number[], t: number): number {
  return 0
}

// 2. loss(p): the mean squared error of the model over all the measurements.
function loss(p: number[]): number {
  return 0
}

// 3. numericGradient(f, p): the partial derivative of f for EACH parameter,
//    by nudging one parameter at a time:  (f(p with p[i] + h) − f(p with p[i] − h)) / 2h,  h = 1e-5
function numericGradient(f: (p: number[]) => number, p: number[]): number[] {
  return p.map(() => 0)
}

console.log(loss([0, 0, 0]), numericGradient(loss, [0, 0, 0]))
`,
  solution: `${THROW_DATA}
plot.scatter(times.map((t, i) => [t, heights[i]]), { title: "measured heights", xLabel: "time (s)", yLabel: "height (m)" })

function model(p: number[], t: number): number {
  return p[0] + p[1] * t + p[2] * t * t
}

function loss(p: number[]): number {
  let total = 0
  for (let i = 0; i < times.length; i++) total += (model(p, times[i]) - heights[i]) ** 2
  return total / times.length
}

function numericGradient(f: (p: number[]) => number, p: number[]): number[] {
  const h = 1e-5
  return p.map((_, i) => {
    const up = p.slice()
    const down = p.slice()
    up[i] += h
    down[i] -= h
    return (f(up) - f(down)) / (2 * h)
  })
}

console.log(loss([0, 0, 0]), numericGradient(loss, [0, 0, 0]))
`,
  tests: `
test("model([1, 2, 3], 2) is 1 + 4 + 12 = 17", () => expect(model([1, 2, 3], 2), "model([1, 2, 3], 2)").toBe(17))
test("the true parabola has a small loss (just the noise)", () => expect(loss([1.5, 11, -4.9]), "loss(true params)").toBeLessThan(0.05))
test("a flat line at 0 has a big loss", () => expect(loss([0, 0, 0]), "loss([0, 0, 0])").toBeGreaterThan(20))
test("numericGradient of x² + 3y at (2, 1) is [4, 3]", () => expect(numericGradient(p => p[0] ** 2 + 3 * p[1], [2, 1]), "gradient").toBeCloseTo([4, 3], 1e-4))
test("numericGradient doesn't change p", () => {
  const __p = [1, 2, 3]
  numericGradient(q => q[0] * q[1] * q[2], __p)
  expect(__p, "p afterwards").toEqual([1, 2, 3])
})
`,
}

export const fitDescend: ExerciseDef = {
  starter: `${THROW_DATA}

// (from Part 1)
function model(p: number[], t: number): number {
  return p[0] + p[1] * t + p[2] * t * t
}
function loss(p: number[]): number {
  let total = 0
  for (let i = 0; i < times.length; i++) total += (model(p, times[i]) - heights[i]) ** 2
  return total / times.length
}
function numericGradient(f: (p: number[]) => number, p: number[]): number[] {
  const h = 1e-5
  return p.map((_, i) => {
    const up = p.slice(), down = p.slice()
    up[i] += h
    down[i] -= h
    return (f(up) - f(down)) / (2 * h)
  })
}

// fit(steps, lr): start at p = [0, 0, 0] and run gradient descent on the loss.
// Record the loss every step in "history" so you can watch it fall.
function fit(steps: number, lr: number): { p: number[]; history: number[] } {
  let p = [0, 0, 0]
  const history: number[] = []
  // your loop here
  return { p, history }
}

const { p, history } = fit(4000, 0.05)
console.log("a, b, c =", p.map(v => round(v, 3)))
plot.line(history.map(Math.log10), { title: "log10(loss) while fitting", xLabel: "step" })
plot.fn([t => model(p, t)], [0, 2.4], { title: "the fitted parabola" })
`,
  solution: `${THROW_DATA}

function model(p: number[], t: number): number {
  return p[0] + p[1] * t + p[2] * t * t
}
function loss(p: number[]): number {
  let total = 0
  for (let i = 0; i < times.length; i++) total += (model(p, times[i]) - heights[i]) ** 2
  return total / times.length
}
function numericGradient(f: (p: number[]) => number, p: number[]): number[] {
  const h = 1e-5
  return p.map((_, i) => {
    const up = p.slice(), down = p.slice()
    up[i] += h
    down[i] -= h
    return (f(up) - f(down)) / (2 * h)
  })
}

function fit(steps: number, lr: number): { p: number[]; history: number[] } {
  let p = [0, 0, 0]
  const history: number[] = []
  for (let s = 0; s < steps; s++) {
    history.push(loss(p))
    const g = numericGradient(loss, p)
    p = p.map((v, i) => v - lr * g[i])
  }
  return { p, history }
}

const { p, history } = fit(4000, 0.05)
console.log("a, b, c =", p.map(v => round(v, 3)))
plot.line(history.map(Math.log10), { title: "log10(loss) while fitting", xLabel: "step" })
plot.fn([t => model(p, t)], [0, 2.4], { title: "the fitted parabola" })
`,
  tests: `
test("the loss goes down", () => {
  const { history: __h } = fit(200, 0.05)
  expect(__h[__h.length - 1], "final loss").toBeLessThan(__h[0] / 10)
})
test("it recovers the curve: c ≈ −4.9 (half of gravity!)", () => expect(fit(4000, 0.05).p[2], "c").toBeBetween(-5.3, -4.5))
test("and the throw speed b ≈ 11 m/s", () => expect(fit(4000, 0.05).p[1], "b").toBeBetween(10.2, 11.8))
test("history has one loss per step", () => expect(fit(50, 0.05).history, "history").toHaveLength(50))
`,
}

export const fitLanding: ExerciseDef = {
  starter: `// Part 3 — use the fitted model. Here are the parameters gradient descent found:
const p = [1.5, 11, -4.9]   // replace with your own fit's numbers if you like
const h = (t: number) => p[0] + p[1] * t + p[2] * t * t

// 1. peakTime(): when is the ball highest? The slope h'(t) = b + 2c·t is 0 there.
//    Solve b + 2c·t = 0 for t (algebra from Module 2!).
function peakTime(): number {
  return 0
}

// 2. landingTime(): when does it hit the ground (h(t) = 0)? Search between the peak and t = 5
//    with bisection: h is going DOWN there, so if h(mid) > 0 the answer is to the right.
function landingTime(): number {
  return 0
}

// 3. distanceTravelled(): total vertical distance from t = 0 to landing (up, then down).
//    The speed is |h'(t)|: add up |h'(t)| · dt over 10 000 small steps.
function distanceTravelled(): number {
  return 0
}

console.log("peak at", round(peakTime(), 3), "s, height", round(h(peakTime()), 2), "m")
console.log("lands at", round(landingTime(), 3), "s")
console.log("travels", round(distanceTravelled(), 2), "m in total")
`,
  solution: `const p = [1.5, 11, -4.9]
const h = (t: number) => p[0] + p[1] * t + p[2] * t * t

function peakTime(): number {
  return -p[1] / (2 * p[2])
}

function landingTime(): number {
  let lo = peakTime()
  let hi = 5
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2
    if (h(mid) > 0) lo = mid
    else hi = mid
  }
  return (lo + hi) / 2
}

function distanceTravelled(): number {
  const end = landingTime()
  const n = 10000
  const dt = end / n
  let total = 0
  for (let i = 0; i < n; i++) {
    const t = (i + 0.5) * dt
    total += Math.abs(p[1] + 2 * p[2] * t) * dt
  }
  return total
}

console.log("peak at", round(peakTime(), 3), "s, height", round(h(peakTime()), 2), "m")
console.log("lands at", round(landingTime(), 3), "s")
console.log("travels", round(distanceTravelled(), 2), "m in total")
`,
  tests: `
test("peak at t = 11 / 9.8 ≈ 1.122 s", () => expect(peakTime(), "peakTime()").toBeCloseTo(11 / 9.8, 1e-6))
test("lands at ≈ 2.377 s", () => expect(landingTime(), "landingTime()").toBeCloseTo((11 + Math.sqrt(121 + 4 * 4.9 * 1.5)) / 9.8, 1e-6))
test("the height there really is 0", () => expect(Math.abs(1.5 + 11 * landingTime() - 4.9 * landingTime() ** 2), "|h(landing)|").toBeLessThan(1e-6))
test("up to the peak and back down to the ground ≈ 13.85 m", () => {
  const __peakH = 1.5 + 121 / (4 * 4.9)
  expect(distanceTravelled(), "distanceTravelled()").toBeCloseTo((__peakH - 1.5) + __peakH, 1e-3)
})
`,
}
